import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { formatJobAssignee, formatJobDate, formatJobMoney } from '../utils/jobPresentation';
import { jobStatusLabel } from './jobUi';
import { formatTime } from './dispatchUi';
import { FlatContractBadge } from '../components/FlatContractBadge';
import { Toast } from '../components/Toast';
import { JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';

const PAGE_SIZE = 20;
const statuses = ['', 'DRAFT', 'WORK_ORDER_GENERATED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED', 'CANCELLED'];

export function JobListPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const status = params.get('status') || '';
  const customerId = params.get('customerId') || '';
  const assignedTo = params.get('assignedTo') || '';
  const search = params.get('search') || '';
  const [searchInput, setSearchInput] = useState(search);
  const [jobs, setJobs] = useState<types.Job[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPages, setCustomerPages] = useState(1);
  const [users, setUsers] = useState<types.AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setParams(next);
  };

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput.trim() !== search) updateParam('search', searchInput.trim());
    }, 350);
    return () => window.clearTimeout(timer);
    // URL changes are intentionally owned by the debounced input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([api.listCustomers(customerPage, 100), api.listAssignableUsers()]).then(([customerResult, userResult]) => {
      if (!active) return;
      if (customerResult.status === 'fulfilled') {
        setCustomers((previous) => customerPage === 1 ? customerResult.value.data : [...previous, ...customerResult.value.data.filter((customer) => !previous.some((saved) => saved.id === customer.id))]);
        setCustomerPages(Math.max(1, customerResult.value.pagination.pages));
      }
      if (userResult.status === 'fulfilled') setUsers(userResult.value);
    });
    return () => { active = false; };
  }, [api, customerPage]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.listJobs(page, PAGE_SIZE, {
      status: status ? status as types.JobStatus : undefined,
      customerId: customerId || undefined,
      assignedTo: assignedTo || undefined,
      search: search || undefined,
    }).then((response) => {
      if (!active) return;
      setJobs(response.data);
      setPagination(response.pagination);
    }).catch((err) => {
      if (active) setError(getErrorMessage(err, 'Failed to load jobs'));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, page, status, customerId, assignedTo, search, reload]);

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page'); else next.set('page', String(nextPage));
    setParams(next);
  };
  const office = user?.role === types.UserRole.ADMIN || user?.role === types.UserRole.OFFICE_MANAGER;
  const pages = Math.max(1, pagination?.pages ?? 1);

  return <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
      {office && <Link to="/jobs/new" className={`${primaryButtonClass} inline-flex items-center justify-center min-h-[44px] sm:min-h-0`}>New Job</Link>}
    </div>
    <section className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div><label htmlFor="job-search" className={labelClass}>Search all jobs</label><input id="job-search" className={inputClass} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Job ID, customer, address" /></div>
        <div><label htmlFor="job-status" className={labelClass}>Status</label><select id="job-status" className={inputClass} value={status} onChange={(event) => updateParam('status', event.target.value)}>{statuses.map((option) => <option key={option} value={option}>{option ? jobStatusLabel(option) : 'All statuses'}</option>)}</select></div>
        <div><label htmlFor="job-customer" className={labelClass}>Customer</label><select id="job-customer" className={inputClass} value={customerId} onChange={(event) => updateParam('customerId', event.target.value)}><option value="">All customers</option>{customerId && !customers.some((customer) => customer.id === customerId) && <option value={customerId}>Selected customer</option>}{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select>{customerPage < customerPages && <button type="button" className="mt-1 py-2 sm:py-0 text-sm text-blue-700 underline" onClick={() => setCustomerPage((current) => current + 1)}>Load more customers</button>}</div>
        <div><label htmlFor="job-assignee" className={labelClass}>Assignee</label><select id="job-assignee" className={inputClass} value={assignedTo} onChange={(event) => updateParam('assignedTo', event.target.value)}><option value="">All assignees</option>{assignedTo && !users.some((candidate) => candidate.id === assignedTo) && <option value={assignedTo}>Selected assignee</option>}{users.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.email}</option>)}</select></div>
      </div>
      {error && <><Toast kind="error">{error}</Toast><p className="py-12 text-center text-gray-600">Jobs could not be loaded. <button type="button" onClick={() => setReload((value) => value + 1)} className="underline ml-2">Retry</button></p></>}
      {loading ? <p role="status" className="py-12 text-center text-gray-600">Loading jobs...</p> : !error && <>
        <p className="text-sm text-gray-600 mb-3">{pagination?.total ?? 0} job{pagination?.total === 1 ? '' : 's'} found</p>
        {jobs.length === 0 ? <p className="py-12 text-center text-gray-600">No jobs match these filters.</p> : <div className="overflow-x-auto"><table className="responsive-table min-w-full divide-y divide-gray-200 text-sm"><thead className="bg-gray-50"><tr>{['Customer', 'Property', 'Status', 'Scheduled', 'Assignee', 'Total'].map((heading) => <th key={heading} scope="col" className="px-4 py-3 text-left text-gray-600 font-medium">{heading}</th>)}</tr></thead><tbody className="divide-y divide-gray-200">{jobs.map((job) => <tr key={job.id} className="hover:bg-gray-50"><td data-label="Customer" data-primary className="px-4 py-3 font-semibold break-words"><Link to={`/jobs/${job.id}`} className="text-blue-700 hover:underline">{job.customer?.name || job.customerId}</Link></td><td data-label="Property" className="px-4 py-3 break-words">{job.property?.address || job.propertyId}</td><td data-label="Status" className="px-4 py-3"><span className="flex flex-wrap items-center gap-2">{jobStatusLabel(job.status)}{job.coveredByFlatContract && <FlatContractBadge />}<JobEnrollmentChip job={job} linked={office} /></span></td><td data-label="Scheduled" className="px-4 py-3">{formatJobDate(job.scheduledDate)}{job.scheduledStartTime && <span className="block text-xs text-gray-600">{formatTime(job.scheduledStartTime)}</span>}</td><td data-label="Assignee" className="px-4 py-3 break-words">{formatJobAssignee(job.assignedToUser)}</td><td data-label="Total" className="px-4 py-3">{formatJobMoney(job.total)}</td></tr>)}</tbody></table></div>}
        {pages > 1 && <div className="flex flex-wrap items-center justify-between gap-3 mt-6"><button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button><span className="text-sm text-gray-600">Page {page} of {pages}</span><button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button></div>}
      </>}
    </section>
  </main>;
}
